import React from 'react'
import isEqual = require("lodash/isEqual");
import {reduce} from 'bluebird';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {isPromise} from "rxjs/util/isPromise";
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/observable/fromPromise';

import * as definedRules from 'core/form/rules';
import {FieldObjectType} from "core/form/form";

type RuleArgsType = {
  value?: any;
  qualifyExpr?: string;
  [key: string]: any;
}

interface RuleType {
  fn: any;
  name?: string;
  args: RuleArgsType;
}

// {fn, name: fn.name, args: {value, qualifyExpr, ...args}}

export interface ValidationResult {
  valid?: boolean;
  error?: string;
  rule?: string;
  rules?: RuleType[];
}


/**
 * Vytvori RuleType[] ze vstupu (field a Validate children)
 * @param children
 * @param field
 * @param value
 * @returns {RuleType[]}
 */
function createRules(children, field: FieldObjectType, value): RuleType[] {
  let rules: RuleType[] = React.Children.toArray(children).filter(c => c).map((c: React.ReactElement<any>) => {
    return {fn: c.type, args: {value, ...c.props}, name: (<any>c.type).name}
  });

  // pridame definition validatory
  if (field.validators) {
    for (let v of field.validators) {
      let fn = definedRules[v.validation];
      if (!fn) throw new Error('Rule ' + v.validation + ' is not defined');
      rules.push({fn, name: fn.name, args: {value, qualifyExpr: v.qualifyExpr, ...v.parameters}});
    }
  }

  // console.log('rules ', rules);

  // validate the rules
  for (let r of rules) {
    if (typeof r.fn !== 'function') {
      throw new Error('Rule type is not a function');// TODO better message
    }
  }
  return rules;
}


function validate(rules: RuleType[], dataObject: object): Promise<ValidationResult> {
  return new Promise((resolve, reject) => {
    let asyncRules = [];
    let wasPromise = false;

    let warningResult: ValidationResult;

    // resolve all synchronous rules first to prevent server load, save async
    // rules to array to deal with later
    for (let rule of rules) {
      let {fn, args, name} = rule;
      let {qualifyExpr, ...otherArgs} = args;
      let qualifyResult = true;
      if (qualifyExpr && dataObject) {
        const that = dataObject;  // saby this bylo dostupne v eval, tedy je tam dustupne jako that
        qualifyResult = eval(qualifyExpr);
      }
      let result = (qualifyResult) ? fn(otherArgs) : {valid: true};
      if (wasPromise || isPromise(result)) {
        wasPromise = true;
        asyncRules.push({name, result: Promise.resolve(result)})
      } else {
        let {valid, reason} = result;
        if (!valid) {
          resolve({valid: false, error: reason, rule: name, rules});
          return
        } else if (valid && reason) {
          warningResult = {valid, error: reason, rule: name, rules};
        }
      }
    }

    reduce(asyncRules, (isOk, {name, result}) => {
      if (!isOk) {
        return false
      } else {
        return result
          .then(({valid, reason}) => {
            if (!valid) {
              resolve({valid: false, error: reason, rule: name, rules});
              return false
            } else {
              if (!warningResult && reason) warningResult = {valid, error: reason, rule: name, rules};
              return true
            }
          })
      }
    }, true).then((isOk) => {
      if (isOk) {
        let res: ValidationResult = {valid: true, rules};
        if (warningResult) {
          res.error = warningResult.error;
          res.rule = warningResult.rule;
        }
        resolve(res);
      }
    })
  })
}


interface ValidateProps {
  children?: JSX.Element | JSX.Element[];
  needTouch?: [string, string][]; // [ruleName, ruleValuePropsName][]
  field: FieldObjectType;
  value: any;
  dataObject?: object;
  onSuccess?: () => void
};


export class Validate extends React.Component<ValidateProps, any> {

  private subjectStream: Subject<RuleType[]>;

  private cachedRules: RuleType[];

  componentDidMount() {

    let {children, value} = this.props;

    //console.log('Validate componentDidMount ', field.name);

    this.subjectStream = new Subject<RuleType[]>();

    this.cachedRules = createRules(children, this.props.field, value);

    this.subjectStream
      .startWith(this.cachedRules)
      //.debounceTime(300) -- kdyz tam je tohle, tak styledDatePicker, ktery emituje onChange az na blur (ne hned pri psani) nestihne zmenit validacni stav pred uzivenim formulare
      .switchMap((rules) => Observable.fromPromise(validate(rules, this.props.dataObject)))
      .subscribe((validationResult: ValidationResult) => {
        //console.debug("field.touched = %s !field.disableValidation = %s", this.props.field.touched, !this.props.field.disableValidation);
        this.props.field.handleValidation({
          validationResult,
          showValidation: this.props.field.touched && !this.props.field.disableValidation //allTouched(validationResult.rules, this.props.needTouch, this.touched)
        }, 'startWith');
        if (this.props.onSuccess && this.props.field.touched && !this.props.field.disableValidation && validationResult.valid) {
          this.props.onSuccess();
        }
      });

  }

  componentWillUnmount() {
    this.props.field.valid = undefined;
    this.subjectStream.unsubscribe();
    this.cachedRules = undefined;
  }

  componentWillReceiveProps(nextProps) {
    let {value, field} = this.props;
    let {field: nextField, value: nextValue, dataObject} = nextProps;

    //console.log('Validate componentWillReceiveProps field = %s, field.disableValidation = %s, nextField .disableValidation= %s, value = %s, nextValue = %s, cachedRules = %o ', field.name, field.disableValidation, nextField.disableValidation, value, nextValue, this.cachedRules);

   if (!isEqual(value, nextValue)) {
   // if (value !== nextValue) { - strict equality zpusobi nekonecne cykly na invoice

      for (let rule of this.cachedRules) {
        rule.args.value = nextValue;
      }

      nextField.touched = true;
      nextField.props.validating = true;
      this.subjectStream.next(this.cachedRules)
    }
  }

  render() {
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/core/form/validate.ts